import type { PlannerProjection, PaceStatus } from "../../lib/revenuePlannerEngine";
import { formatUSD } from "@/utils/formatters";

// =============================================================================
// ProjectionSummary — Bottom Bar Showing Total Projected vs Goal
// =============================================================================

interface ProjectionSummaryProps {
  goalAmount: number;
  projection: PlannerProjection;
  paceProjected: number;
  paceStatus: PaceStatus;
}

export default function ProjectionSummary({
  goalAmount,
  projection,
  paceProjected,
  paceStatus,
}: ProjectionSummaryProps) {
  if (goalAmount <= 0) return null;

  const projectedColor =
    projection.pct_to_goal >= 100
      ? "var(--green, #16a34a)"
      : projection.pct_to_goal >= 80
        ? "var(--accent, #2563eb)"
        : "var(--red, #dc2626)";

  const gapColor = projection.gap_to_goal <= 0 ? "var(--green, #16a34a)" : "var(--red, #dc2626)";

  const paceColor =
    paceStatus === "ahead"
      ? "var(--green, #16a34a)"
      : paceStatus === "on_track"
        ? "var(--accent, #2563eb)"
        : "var(--red, #dc2626)";

  return (
    <div
      className="grid grid-cols-2 gap-4 liquid-glass anim-lift rounded-[14px]"
      style={{ padding: "20px 24px" }}
    >
      {/* Left: Current pace */}
      <div>
        <div
          className="font-semibold uppercase tracking-wide mb-1"
          style={{
            fontSize: 11,
            color: "var(--text-secondary)",
            letterSpacing: "0.08em",
          }}
        >
          At Current Pace
        </div>
        <p style={{ fontSize: 9, color: "var(--text-tertiary)", marginBottom: 4 }}>
          If we keep doing exactly what we&apos;re doing, this is where we land by end of month
        </p>
        <div className="flex items-baseline gap-2">
          <span className="font-bold" style={{ fontSize: 22, color: paceColor }}>
            {formatUSD(paceProjected)}
          </span>
          <span style={{ fontSize: 11, color: "var(--text-tertiary)" }}>
            of {formatUSD(goalAmount)} goal
          </span>
        </div>
      </div>

      {/* Right: Scenario projection */}
      <div>
        <div
          className="font-semibold uppercase tracking-wide mb-1"
          style={{
            fontSize: 11,
            color: "var(--text-secondary)",
            letterSpacing: "0.08em",
          }}
        >
          Scenario Projection
        </div>
        <p style={{ fontSize: 9, color: "var(--text-tertiary)", marginBottom: 4 }}>
          What happens if you adjust the sliders above — this is the modeled outcome by EOM
        </p>
        <div className="flex items-baseline gap-3">
          <span className="font-bold" style={{ fontSize: 22, color: projectedColor }}>
            {formatUSD(projection.total_projected)}
          </span>
          <span
            className="font-semibold rounded"
            style={{
              fontSize: 11,
              color: projectedColor,
              background: `color-mix(in srgb, ${projectedColor} 8%, transparent)`,
              padding: "2px 8px",
            }}
          >
            {projection.pct_to_goal.toFixed(0)}%
          </span>
          <span className="font-medium" style={{ fontSize: 12, color: gapColor }}>
            {projection.gap_to_goal > 0
              ? `-${formatUSD(projection.gap_to_goal)} gap`
              : `+${formatUSD(Math.abs(projection.gap_to_goal))} surplus`}
          </span>
        </div>
      </div>
    </div>
  );
}
